import { createServerFn } from "@tanstack/react-start";
import { MembershipPlan } from "~/lib/schemas";

const MAX_COACHES = 12;

export const getCoaches = createServerFn({ method: "GET" }).handler(
  async () => {
    const { getDBWithInit } = await import("~/lib/db");
    const { profiles, testimonials, certifications, offers, coachingResults } =
      await import("~/lib/schemas");

    const coaches = await (
      await getDBWithInit()
    ).query.coaches.findMany({
      where: (coaches, { and, isNotNull }) =>
        and(
          isNotNull(coaches.fullName),
          isNotNull(coaches.ghlCalendarId),
          isNotNull(coaches.profilePictureUrl), // Ensure they have a profile picture
        ),
      with: {
        profile: true,
        results: true,
        mainOffer: true,
        certifications: true,
        testimonials: true,
      },
      orderBy: (coaches, { desc, sql }) => [
        // Pro and ProPlus members first
        desc(
          sql`CASE WHEN ${coaches.membershipPlan} = ${MembershipPlan.ProPlus} THEN 3 WHEN ${coaches.membershipPlan} = ${MembershipPlan.Pro} THEN 2 ELSE 1 END`,
        ),
        // Profile quality score based on multiple factors
        desc(
          sql`(
            -- Base profile points (up to 30)
            CASE WHEN COALESCE((SELECT ${sql.raw(
              profiles.coachingTitle.name,
            )} FROM ${profiles} WHERE ${sql.raw(profiles.coachId.name)} = ${
              coaches.id
            } LIMIT 1), '') != '' THEN 10 ELSE 0 END +
            CASE WHEN COALESCE((SELECT ${sql.raw(
              profiles.title.name,
            )} FROM ${profiles} WHERE ${sql.raw(profiles.coachId.name)} = ${
              coaches.id
            } LIMIT 1), '') != '' THEN 10 ELSE 0 END +
            CASE WHEN COALESCE((SELECT ${sql.raw(
              profiles.description.name,
            )} FROM ${profiles} WHERE ${sql.raw(profiles.coachId.name)} = ${
              coaches.id
            } LIMIT 1), '') != '' THEN 10 ELSE 0 END +

            -- Description length points (up to 20)
            CASE
              WHEN LENGTH(COALESCE((SELECT ${sql.raw(
                profiles.description.name,
              )} FROM ${profiles} WHERE ${sql.raw(profiles.coachId.name)} = ${
                coaches.id
              } LIMIT 1), '')) > 500 THEN 20
              WHEN LENGTH(COALESCE((SELECT ${sql.raw(
                profiles.description.name,
              )} FROM ${profiles} WHERE ${sql.raw(profiles.coachId.name)} = ${
                coaches.id
              } LIMIT 1), '')) > 200 THEN 10
              ELSE 0
            END +

            -- Social proof points
            COALESCE((SELECT COUNT(*) * 5 FROM ${testimonials} WHERE ${sql.raw(
              testimonials.coachId.name,
            )} = ${coaches.id}), 0) +
            COALESCE((SELECT COUNT(*) * 3 FROM ${certifications} WHERE ${sql.raw(
              certifications.coachId.name,
            )} = ${coaches.id}), 0) +

            -- Offer quality points (up to 30)
            CASE
              WHEN COALESCE((SELECT ${sql.raw(
                offers.headline.name,
              )} FROM ${offers} WHERE ${offers.id} = ${
                coaches.mainOfferId
              } LIMIT 1), '') != '' AND
                   LENGTH(COALESCE((SELECT ${sql.raw(
                     offers.description.name,
                   )} FROM ${offers} WHERE ${offers.id} = ${
                     coaches.mainOfferId
                   } LIMIT 1), '')) > 200
                THEN 30
              WHEN COALESCE((SELECT ${sql.raw(
                offers.headline.name,
              )} FROM ${offers} WHERE ${offers.id} = ${
                coaches.mainOfferId
              } LIMIT 1), '') != ''
                THEN 15
              ELSE 0
            END +

            -- Results points
            COALESCE((SELECT COUNT(*) * 4 FROM ${coachingResults} WHERE ${sql.raw(
              coachingResults.coachId.name,
            )} = ${coaches.id}), 0)
          )`,
        ),
        // Last update time as tiebreaker
        desc(coaches.updatedAt),
      ],
      limit: MAX_COACHES + 1, // Get one extra to check if there are more
    });

    // Calculate profile completeness score for better filtering
    const filteredCoaches = coaches
      .filter((coach) => {
        // Basic profile requirements
        if (
          !coach.profile?.coachingTitle ||
          !coach.profile.title ||
          !coach.profile.description ||
          !coach.mainOffer?.headline
          // || !coach.mainOffer.description
        ) {
          return false;
        }

        // Additional quality checks
        const hasDetailedProfile =
          100 < (coach.profile.description?.length ?? 0) && // Decent description length
          0 < coach.certifications.length && // Has certifications
          1 < coach.testimonials.length; // Has multiple testimonials

        return hasDetailedProfile;
      })
      .slice(0, MAX_COACHES);
    //create array from the same item

    return {
      coaches: filteredCoaches,
      /* coaches: Array(5)
        .fill(null)
        .map(() => ({ ...filteredCoaches[0] })), */
    };
  },
);

import { notFound } from "@tanstack/react-router";

import type {
  CoachSelect,
  CertificationsSelect,
  TestimonialSelect,
  OfferSelect,
  CoachingResultsSelect,
  OfferTagSelect,
  OfferBenefitSelect,
} from "~/../drizzle/schema";
import type { CoachDetailsSelect, ProfileSelect } from "~/../drizzle/schema";
import type { ResourceSelect } from "~/../drizzle/schema";

type Offer = OfferSelect & {
  tags: OfferTagSelect[];
  benefits: OfferBenefitSelect[];
};

// Public coach profile - excludes all sensitive/internal fields
export type CoachPublicProfile = Omit<
  CoachSelect,
  | "ghlContactId"
  | "ghlLocationId"
  | "ghlUserId"
  | "stripeCustomerId"
  | "stripeAccountId"
  | "stripeSubscriptionId"
  | "stripePayoutsEnabled"
  | "stripeChargesEnabled"
  | "contractSigned"
  | "paymentTestApiKey"
  | "paymentTestPublishableKey"
  | "paymentLiveApiKey"
  | "paymentLivePublishableKey"
  | "createdAt"
  | "updatedAt"
  | "email"
  | "phone"
>;

// Internal coach data with all fields (for backend/authenticated use)
export type CoachPrivateData = CoachSelect & {
  profile: ProfileSelect | null;
  details: CoachDetailsSelect | null;
  resources: ResourceSelect[];
  certifications: CertificationsSelect[];
  testimonials: TestimonialSelect[];
  results: CoachingResultsSelect[];
  mainOffer: Offer | null;
};

// Public coach data for public-facing pages
export type CoachPublicData = CoachPublicProfile & {
  profile: ProfileSelect;
  details: CoachDetailsSelect;
  resources: ResourceSelect[];
  certifications: CertificationsSelect[];
  testimonials: TestimonialSelect[];
  results: CoachingResultsSelect[];
  mainOffer: Offer | null;
};

// Legacy types for backwards compatibility - prefer CoachPublicData and CoachPrivateData
export type CoachData = CoachPrivateData;
export type CoachPublicProfileData = CoachPublicData;

export const getCoachData = createServerFn({ method: "GET" })
  .validator((data: { username: string }) => data)
  .handler(async ({ data }) => {
    const { username } = data;
    const { getCoachByUsername } = await import("~/lib/server/coach");
    const coach = await getCoachByUsername(username);
    if (!coach?.profile) {
      throw notFound();
    }

    // Return public data only - the getCoachByUsername function already filters out sensitive fields
    return coach as CoachPublicData;
  });

export const getOtherOffers = createServerFn({ method: "GET" })
  .validator(
    (data: {
      coachId: string;
      mainOfferId?: string | null;
      membershipPlan: MembershipPlan;
    }) => data,
  )
  .handler(async ({ data }) => {
    const { coachId, mainOfferId, membershipPlan } = data;

    if (membershipPlan !== MembershipPlan.ProPlus || !mainOfferId) {
      return [];
    }

    const { getDBWithInit } = await import("~/lib/db");
    const offers = await (
      await getDBWithInit()
    ).query.offers.findMany({
      where: (offers, { eq, and, not }) =>
        and(
          eq(offers.coachId, coachId),
          mainOfferId ? not(eq(offers.id, mainOfferId)) : undefined,
        ),
      columns: {
        id: true,
        headline: true,
        description: true,
      },
      with: {
        tags: {
          columns: {
            id: true,
            title: true,
          },
        },
      },
    });

    return offers;
  });

export const getOfferByUsernameAndId = createServerFn({ method: "GET" })
  .validator((data: { username: string; offerId: string }) => data)
  .handler(async ({ data }) => {
    const { username, offerId } = data;
    const { getCoachByUsername } = await import("~/lib/server/coach");
    const { getDBWithInit } = await import("~/lib/db");

    const coach = await getCoachByUsername(username);

    if (!coach) {
      throw new Response("Coach not found", { status: 404 });
    }

    const offer = await (
      await getDBWithInit()
    ).query.offers.findFirst({
      where: (offers, { eq, and }) =>
        and(eq(offers.id, offerId), eq(offers.coachId, coach.id)),
      with: {
        benefits: { orderBy: (x, { asc }) => asc(x.order) },
        components: { orderBy: (x, { asc }) => asc(x.order) },
        tags: { orderBy: (x, { asc }) => asc(x.order) },
        bonuses: { orderBy: (x, { asc }) => asc(x.order) },
        faqs: { orderBy: (x, { asc }) => asc(x.order) },
      },
    });

    if (!offer) {
      throw new Response("Offer not found", { status: 404 });
    }

    return { coach: coach as CoachPublicData, offer };
  });
